import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {DeleteButtonWithConfirmation, ShowButton} from 'components/refinedev/action_buttons';
import {defaultQueryOptions} from 'pages/_utils';
import StateBoundary from '@/components/basic/StateBoundary';
import formatter from '@/lib/formatter';
import {DataTableColumnHeader} from '@/components/data-table/data-table-column-header';
import {DataTable} from '@/components/data-table/data-table';
import DefaultDataTableToolbar from '@/components/refinedev/data-table/DefaultDataTableToolbar';
import {BaseKey, BaseRecord} from '@refinedev/core';
import {CreateTaskDialogButton} from '../tasks/components/task-create-dialog-button';
import {useMemo, useState} from 'react';
import useRefineDataTable from '@/hooks/refinedev/useRefineDataTable';
import {CircleMinusIcon, CirclePlusIcon} from 'lucide-react';
import {UserIconList} from '@/pages/_shared/user-icon-list';
import {TaskStatuses} from '../tasks/types';
import {TaskEditDialogButton} from '../tasks/components/task-edit-dialog-button';

const thisResource = 'tasks';

const columnHelper = createColumnHelper<any>();

const columns = [
  // columnHelper.accessor('id', {
  //   meta: {label: 'ID'},
  //   header: ({column}) => <DataTableColumnHeader column={column} />,
  //   cell: info => info.getValue(),
  // }),
  columnHelper.accessor('name', {
    meta: {label: 'Tên công việc'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: ({row, getValue}) => (
      <div
        className="flex flex-row items-center"
        style={{paddingLeft: `${row.original.depth * 2}rem`}}>
        {row.getCanExpand() && (
          <button onClick={() => row.toggleExpanded()} className="mr-1 p-0 hover:text-blue-500">
            {row.getIsExpanded() ? (
              <CircleMinusIcon className="w-4 h-4" />
            ) : (
              <CirclePlusIcon className="w-4 h-4" />
            )}
          </button>
        )}
        {getValue()}
      </div>
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('description', {
    meta: {label: 'Mô tả'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
    enableSorting: false,
  }),
  // các cột kế hoạch
  columnHelper.accessor('start_date', {
    meta: {label: 'Bắt đầu (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('expected_work_days', {
    meta: {label: 'Số ngày (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('initial_end_date', {
    meta: {label: 'Kết thúc (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // các cột kế hoạch điều chỉnh
  columnHelper.accessor('rescheduled_start_date', {
    meta: {label: 'Bắt đầu (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('rescheduled_work_days', {
    meta: {label: 'Số ngày (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('rescheduled_end_date', {
    meta: {label: 'Kết thúc (KHĐC)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // các cột thực tế
  columnHelper.accessor('actual_start_date', {
    meta: {label: 'Bắt đầu (TT)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  columnHelper.accessor('actual_work_days', {
    meta: {label: 'Số ngày (KH)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('actual_end_date', {
    meta: {label: 'Kết thúc (TT)'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDate(info.getValue()),
  }),
  // ngân sách
  columnHelper.accessor('requested_budget', {
    meta: {label: 'Ngân sách đề xuất'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('approved_budget', {
    meta: {label: 'Ngân sách phê duyệt'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  //////
  columnHelper.accessor('members', {
    meta: {label: 'Thành viên'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => <UserIconList users={info.getValue()} maxDisplay={4} />,
    enableSorting: false,
  }),
  columnHelper.accessor('status', {
    meta: {label: 'Trạng thái'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => TaskStatuses.find(v => v.id === info.getValue())?.label || '',
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'actions',
    meta: {label: 'Hành động'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: ({row}) => (
      <div className="flex flex-row">
        <ShowButton
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{
            my_role: row.original.my_role,
            my_project_role: row.original.my_project_role,
          }}
        />
        <TaskEditDialogButton
          id={String(row.original.id)}
          canParams={{
            my_role: row.original.my_role,
            my_project_role: row.original.my_project_role,
          }}
        />
        {/* <EditButton id={String(row.original.id)} resource={thisResource} /> */}
        {/* <EditButtonWorkflow id={String(info.row.getValue('id'))} resource={thisResource} /> */}

        <DeleteButtonWithConfirmation
          id={String(row.original.id)}
          resource={thisResource}
          canParams={{
            my_role: row.original.my_role,
            my_project_role: row.original.my_project_role,
          }}
        />
      </div>
    ),
    enableSorting: false,
  }),
];

const TaskListInProject = ({project}: {project: BaseRecord}) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const {
    refineTable,
    data,
    sorting,
    columnVisibility,
    rowSelection,
    pagination,
    globalFilter,
    total,
    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,
    setRowSelection,
    onColumnFiltersChange,
    setColumnVisibility,
  } = useRefineDataTable({
    resource: `projects/${project.id}/tasks`,
    sorters: {initial: [{field: 'id', order: 'asc'}]},
    pagination: {pageSize: 9999},
    // filters: project_id
    //   ? {permanent: [{field: 'project_id', operator: 'eq', value: project_id}]}
    //   : undefined,
    queryOptions: {
      ...defaultQueryOptions,
      enabled: Boolean(project?.id),
    },
    errorNotification: false,
    meta: {
      with: ['members'],
    },
  });

  const treeData = useMemo(() => {
    if (!data) return [];

    const list = [...data];
    const map = new Map<BaseKey | undefined, any>();
    const tree: any[] = [];

    // Create map of items by ID
    list.forEach(item => map.set(item.id, {...item, subRows: [], depth: 0}));

    // Build tree structure
    list.forEach(item => {
      if (item.parent_id && map.has(item.parent_id)) {
        const parent = map.get(item.parent_id);
        parent.subRows.push(map.get(item.id));
        map.get(item.id).depth = parent.depth + 1;
      } else {
        tree.push(map.get(item.id));
      }
    });

    return tree;
  }, [data]);

  const tanstackTable = useReactTable({
    data: treeData,
    columns,
    state: {
      expanded,
      sorting,
      columnVisibility,
      rowSelection,
      // columnFilters,
      pagination,
      globalFilter,
    },
    initialState: {
      columnVisibility: {
        expected_work_days: false,
        initial_end_date: false,
        rescheduled_start_date: false,
        rescheduled_work_days: false,
        rescheduled_end_date: false,
        actual_start_date: false,
        actual_work_days: false,
        requested_budget: false,
      },
    },
    enableRowSelection: true,
    pageCount: Math.ceil(total / refineTable.pageSize),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,

    onPaginationChange,
    onSortingChange,
    onGlobalFilterChange,
    onRowSelectionChange: setRowSelection,

    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,

    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row => row.original.subRows && row.original.subRows.length > 0,
    getSubRows: row => row.subRows, // return the children array as sub-rows
    onExpandedChange: setExpanded,
  });

  return (
    <StateBoundary
      isLoading={refineTable.tableQuery.isLoading}
      error={refineTable.tableQuery.error}>
      <div className="p-2">
        <DataTable
          table={tanstackTable}
          toolbar={
            <DefaultDataTableToolbar
              resource={thisResource}
              table={tanstackTable}
              createButton={
                project?.id ? (
                  <CreateTaskDialogButton
                    project_id={project.id}
                    onSuccess={() => refineTable.tableQuery.refetch()}
                    canParams={{my_project_role: project.my_role}}
                  />
                ) : (
                  false
                )
              }
              
              viewOptionsStorageKey="task-list-project"
            />
          }
        />
      </div>
    </StateBoundary>
  );
};

TaskListInProject.table_columns = columns;

export default TaskListInProject;
